import { Activity, Timer, UserCheck, UserX, Search, LogOut, RefreshCw } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
} from 'recharts';
import API_BASE_URL from '../../config';

function dateOnlyString(dateLike) {
  const d = new Date(dateLike);
  return d.getFullYear() + '-' +
    String(d.getMonth() + 1).padStart(2, '0') + '-' +
    String(d.getDate()).padStart(2, '0');
}
function startOfWeek(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay());
  return new Date(d);
}
function endOfWeek(date) {
  const s = startOfWeek(date);
  s.setDate(s.getDate() + 6);
  return s;
}
function startOfMonth(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(1);
  return d;
}
function endOfMonth(date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  d.setMonth(d.getMonth() + 1, 0);
  return d;
}
function filterAttendance(records, dateRange, customStart, customEnd) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (dateRange === "today") {
    const todayISO = dateOnlyString(today);
    return records.filter(entry => dateOnlyString(entry.checkIn) === todayISO);
  }
  if (dateRange === "week") {
    const weekStart = startOfWeek(today);
    const weekEnd = endOfWeek(today);
    return records.filter(entry => {
      const entryDate = new Date(entry.checkIn);
      entryDate.setHours(0, 0, 0, 0);
      return entryDate >= weekStart && entryDate <= weekEnd;
    });
  }
  if (dateRange === "month") {
    const monthStart = startOfMonth(today);
    const monthEnd = endOfMonth(today);
    return records.filter(entry => {
      const entryDate = new Date(entry.checkIn);
      entryDate.setHours(0, 0, 0, 0);
      return entryDate >= monthStart && entryDate <= monthEnd;
    });
  }
  if (dateRange === "custom") {
    const start = customStart ? new Date(customStart) : null;
    if (start) start.setHours(0, 0, 0, 0);
    const end = customEnd ? new Date(customEnd) : null;
    if (end) end.setHours(23, 59, 59, 999);

    return records.filter(entry => {
      const entryDate = new Date(entry.checkIn);
      if (start && entryDate < start) return false;
      if (end && entryDate > end) return false;
      return true;
    });
  }
  return records;
}

const PAGE_SIZE = 8;

// ---- Graph data creator ----
function createAttendanceGraphData(data, dateRange) {
  if (!data.length) return [];
  if (dateRange === 'today') {
    return [{
      date: dateOnlyString(new Date(data[0]?.checkIn || new Date())),
      Present: data.length
    }];
  }
  const countPerDay = {};
  data.forEach(entry => {
    const dt = dateOnlyString(entry.checkIn);
    countPerDay[dt] = (countPerDay[dt] || 0) + 1;
  });
  const keys = Object.keys(countPerDay).sort();
  return keys.map(d => ({
    date: d,
    Present: countPerDay[d]
  }));
}

const AttendanceTab = () => {
  const location = useLocation();
  const [subMode, setSubMode] = useState(location.state?.attendanceSubMode || 'live'); // 'live' or 'history'

  // History states
  const [dateRange, setDateRange] = useState('today');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [rawAttendance, setRawAttendance] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [page, setPage] = useState(0);

  // Live states
  const [liveSessions, setLiveSessions] = useState([]);
  const [loadingLive, setLoadingLive] = useState(false);
  const [liveSearchQuery, setLiveSearchQuery] = useState('');

  // Fetch history data
  const fetchHistoryData = async () => {
    const userInfo = JSON.parse(localStorage.getItem('fitscan_user'));
    try {
      const token = JSON.parse(localStorage.getItem('accessToken') || '{}').token || '';
      const response = await fetch(`${API_BASE_URL}/api/attendance/all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userInfo.username),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const records = await response.json();
      setRawAttendance(records);
    } catch (err) {
      console.error(err);
    }
  };

  // Fetch live sessions
  const fetchLiveSessions = async () => {
    setLoadingLive(true);
    try {
      const token = JSON.parse(localStorage.getItem('accessToken') || '{}').token || '';
      const response = await fetch(`${API_BASE_URL}/api/attendance/live-sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(''),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setLiveSessions(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingLive(false);
    }
  };

  // Checkout action
  const handleManualCheckout = async (username) => {
    if (!window.confirm(`Are you sure you want to check out @${username}?`)) return;
    try {
      const token = JSON.parse(localStorage.getItem('accessToken') || '{}').token || '';
      const response = await fetch(`${API_BASE_URL}/api/qrcode/checkout/${username}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        fetchLiveSessions();
      } else {
        alert("Checkout failed.");
      }
    } catch (err) {
      console.error(err);
      alert("Error checking out user.");
    }
  };

  const formatXAxisTick = (tickItem) => {
    if (/^\d{4}-\d{2}$/.test(tickItem)) {
      const [year, month] = tickItem.split('-');
      const monthName = new Date(year, month - 1).toLocaleString('default', { month: 'short' });
      return `${year}-${monthName}`;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(tickItem)) {
      const date = new Date(tickItem);
      return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: '2-digit' });
    }
    return tickItem;
  };

  useEffect(() => {
    if (subMode === 'history') {
      fetchHistoryData();
    } else {
      fetchLiveSessions();
    }
  }, [subMode]);

  useEffect(() => {
    if (subMode === 'history') {
      setFilteredData(filterAttendance(rawAttendance, dateRange, customStart, customEnd));
      setPage(0);
    }
  }, [rawAttendance, dateRange, customStart, customEnd, subMode]);

  const filteredLiveSessions = liveSessions.filter(s => {
    if (!liveSearchQuery) return true;
    return s.name?.toLowerCase().includes(liveSearchQuery.toLowerCase());
  });

  const getAvatarInitials = (name) => {
    if (!name) return 'JD';
    const parts = name.split(' ');
    return `${parts[0]?.[0] || ''}${parts[1]?.[0] || ''}`.toUpperCase();
  };

  // Pagination for history
  const total = filteredData.length;
  const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
  const pagedData = filteredData.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const chartData = createAttendanceGraphData(filteredData, dateRange);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-white">
            <Activity size={24} className="text-lime-400" />
            Attendance Tracking
          </h1>
          <p className="text-gray-400 text-sm mt-1">Monitor live occupants and attendance history</p>
        </div>

        {/* Sub-mode selector */}
        <div className="flex bg-[#222] border border-[#2b2b2b] p-1 rounded-lg">
          <button
            onClick={() => setSubMode('live')}
            className={`px-4 py-1.5 rounded text-sm font-semibold transition-all ${subMode === 'live' ? 'bg-lime-400 text-zinc-950 font-bold' : 'text-gray-400 hover:text-white'
              }`}
          >
            Currently in Gym ({liveSessions.length})
          </button>
          <button
            onClick={() => setSubMode('history')}
            className={`px-4 py-1.5 rounded text-sm font-semibold transition-all ${subMode === 'history' ? 'bg-lime-400 text-zinc-950 font-bold' : 'text-gray-400 hover:text-white'
              }`}
          >
            Attendance History
          </button>
        </div>
      </div>

      {subMode === 'live' ? (
        <div className="space-y-4">
          <div className="flex justify-between items-center gap-3">
            <div className="relative flex-1 md:max-w-xs">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search occupants..."
                value={liveSearchQuery}
                onChange={(e) => setLiveSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg focus:outline-none focus:ring-2 focus:ring-lime-400 text-sm text-white"
              />
            </div>
            <button
              onClick={fetchLiveSessions}
              disabled={loadingLive}
              className="p-2.5 bg-[#222] border border-[#2b2b2b] hover:bg-[#2b2b2b] rounded-lg text-gray-400 hover:text-white"
            >
              <RefreshCw size={18} className={loadingLive ? 'animate-spin' : ''} />
            </button>
          </div>

          <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl overflow-hidden shadow-xl">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[600px]">
                <thead>
                  <tr className="bg-[#222] border-b border-[#2b2b2b] text-gray-300 font-semibold text-xs uppercase tracking-wider">
                    <th className="px-6 py-4 w-20">Photo</th>
                    <th className="px-6 py-4">Name</th>
                    <th className="px-6 py-4">Check-in Time</th>
                    <th className="px-6 py-4">Duration Inside</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#2b2b2b] text-sm text-gray-200">
                  {filteredLiveSessions.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center text-gray-500 bg-[#1a1a1a]">
                        No active gym sessions right now.
                      </td>
                    </tr>
                  ) : (
                    filteredLiveSessions.map((session) => (
                      <tr key={session.id} className="hover:bg-[#222]/50 transition-colors">
                        <td className="px-6 py-3">
                          <div className="w-10 h-10 rounded-full bg-lime-400/10 flex items-center justify-center text-lime-400 relative flex-shrink-0">
                            {session.photoUrl ? (
                              <img
                                src={session.photoUrl}
                                alt={session.name}
                                className="w-full h-full object-cover rounded-full"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="text-sm font-bold">
                                {getAvatarInitials(session.name)}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-medium text-white">{session.name}</div>
                          <div className="text-xs text-gray-400">@{session.username}</div>
                        </td>
                        <td className="px-6 py-4 text-gray-300">
                          {new Date(session.checkInTime).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                        </td>
                        <td className="px-6 py-4 font-semibold text-lime-400">
                          {session.duration} mins
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => handleManualCheckout(session.username)}
                            className="bg-red-500/10 hover:bg-red-500/25 border border-red-500/30 hover:border-red-500/50 text-red-400 px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ml-auto cursor-pointer"
                          >
                            <LogOut size={12} />
                            Checkout
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex justify-end items-center gap-3">
            <div className="flex gap-2 bg-[#222] border border-[#2b2b2b] p-1 rounded-lg">
              {['today', 'week', 'month', 'custom'].map((range) => (
                <button
                  key={range}
                  onClick={() => setDateRange(range)}
                  className={`px-3 py-1 rounded text-sm capitalize ${dateRange === range ? 'bg-lime-400 text-[#181f10]' : 'hover:bg-[#2b2b2b]'}`}
                >
                  {range}
                </button>
              ))}
            </div>
            {dateRange === 'custom' && (
              <div className="flex items-center gap-2 bg-[#222] border border-[#2b2b2b] px-3 py-1 rounded-lg text-sm">
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="bg-transparent text-white border-none outline-none focus:ring-0 w-32 cursor-pointer [color-scheme:dark]"
                />
                <span className="text-gray-500">to</span>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="bg-transparent text-white border-none outline-none focus:ring-0 w-32 cursor-pointer [color-scheme:dark]"
                />
              </div>
            )}
          </div>

          <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl overflow-hidden shadow-xl">
            <div className="w-full overflow-x-auto">
              <div className="min-w-[700px] max-h-[300px] overflow-y-auto">
                <div className="grid grid-cols-8 bg-[#222] px-2 py-2 font-medium text-sm sticky top-0 z-10">
                  <div className="col-span-2">Member</div>
                  <div className="col-span-2">Check-in</div>
                  <div className="col-span-2">Check-out</div>
                  <div className="col-span-1">Duration</div>
                  <div className="col-span-1">Status</div>
                </div>
                {pagedData.length === 0 ? (
                  <div className="text-center text-gray-400 p-6">
                    No attendance records for this period.
                  </div>
                ) : (
                  pagedData.map((entry) => (
                    <div key={entry.id} className="grid grid-cols-8 px-2 py-2 border-b border-[#2b2b2b] hover:bg-[#222] items-center text-sm">
                      <div className="col-span-2">{entry.name}</div>
                      <div className="col-span-2 flex items-center gap-2">
                        <UserCheck size={16} className="text-lime-400" />
                        {entry.checkIn ? new Date(entry.checkIn).toLocaleString() : "-"}
                      </div>
                      <div className="col-span-2 flex items-center gap-2">
                        {entry.checkOut ? (
                          <>
                            <UserX size={16} className="text-red-400" />
                            {new Date(entry.checkOut).toLocaleString()}
                          </>
                        ) : (
                          <span className="text-gray-400">Still in gym</span>
                        )}
                      </div>

                      <div className="col-span-1 flex items-center gap-2">
                        {entry.duration ? (
                          <>
                            <Timer size={16} className="text-red-400" />
                            {entry.duration.toLocaleString()}
                          </>
                        ) : (
                          <span className="text-gray-400">NA</span>
                        )}
                      </div>

                      <div className="col-span-1">
                        <span className={`px-2 py-1 rounded-full text-xs ${entry.checkOut ? 'bg-gray-400/10 text-gray-400' : 'bg-lime-400/10 text-lime-400'}`}>
                          {entry.checkOut ? 'Completed' : 'Active'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="flex justify-between items-center px-4 py-2 border-t border-[#222] bg-[#181818]">
              <p className="text-gray-400 text-sm">
                Showing {pagedData.length} of {filteredData.length} records
              </p>
              <div className="flex gap-2 items-center">
                <button
                  className="px-3 py-1 bg-[#222] border border-[#2b2b2b] rounded hover:bg-[#2b2b2b] disabled:opacity-50"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  Previous
                </button>
                <span className="text-gray-400 text-sm">
                  Page {page + 1} of {maxPage + 1}
                </span>
                <button
                  className="px-3 py-1 bg-lime-400 text-[#181f10] rounded hover:bg-lime-300 disabled:opacity-50"
                  onClick={() => setPage(p => Math.min(maxPage, p + 1))}
                  disabled={page >= maxPage}
                >
                  Next
                </button>
              </div>
            </div>
          </div>

          <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl p-4">
            <h2 className="text-lg font-semibold text-white mb-4">Attendance Trends</h2>
            {chartData.length === 0 ? (
              <div className="text-gray-400 text-sm">No attendance data available for selected period.</div>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart
                  data={chartData}
                  barSize={Math.max(20, Math.min(60, Math.floor(300 / (chartData.length || 1))))}
                  barCategoryGap="30%"
                  margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                >
                  <CartesianGrid stroke="#2b2b2b" />
                  <XAxis
                    dataKey="date"
                    stroke="#aaa"
                    tickFormatter={formatXAxisTick}
                    tick={{ fontSize: 11 }}
                    padding={{ left: 20, right: 20 }}
                  />
                  <YAxis stroke="#aaa" allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#222', borderColor: '#2b2b2b', color: '#fff' }}
                  />
                  <Bar dataKey="Present" fill="#84cc16" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceTab;
